import * as React from "react";
import { Plus, Trash2 } from "lucide-react";
import { TextInput } from "../../../ui/components/Form";
import { Switch } from "../../../ui/components/Switch";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { useI18n } from "../../lib/i18n";
import type { CommandParamField } from "../../lib/api/commands";
import * as s from "./commands.css";

export type CommandParamValues = Record<string, unknown>;

type RegisterValue = { register: string; value: string };

export function initialParamValues(fields: CommandParamField[]): CommandParamValues {
  const values: CommandParamValues = {};
  for (const field of fields) {
    if (field.type === "register_list") values[field.key] = [""];
    else if (field.type === "register_values") values[field.key] = [{ register: "", value: "" }];
    else if (field.type === "boolean") values[field.key] = Boolean(field.default ?? false);
    else values[field.key] = field.default != null ? String(field.default) : "";
  }
  return values;
}

/** Fields that must be filled before the form can be sent. */
export function missingRequiredParams(fields: CommandParamField[], values: CommandParamValues): string[] {
  return fields
    .filter((field) => field.required)
    .filter((field) => {
      const v = values[field.key];
      if (field.type === "register_list") return !(v as string[]).some((r) => r.trim());
      if (field.type === "register_values") return !(v as RegisterValue[]).some((r) => r.register.trim());
      if (field.type === "boolean") return false;
      return String(v ?? "").trim() === "";
    })
    .map((field) => field.key);
}

/** Register rows come from the form as strings; the API expects numbers for values. */
export function paramValuesToRequest(fields: CommandParamField[], values: CommandParamValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    const value = values[field.key];
    if (field.type === "register_list") {
      out[field.key] = (value as string[]).filter((r) => r.trim());
    } else if (field.type === "register_values") {
      out[field.key] = (value as Array<{ register: string; value: string }>)
        .filter((r) => r.register.trim())
        .map((r) => ({ register: r.register.trim(), value: r.value.trim() === "" ? null : Number(r.value) }));
    } else {
      out[field.key] = value;
    }
  }
  return out;
}

function FieldLabel({ field }: { field: CommandParamField }) {
  return (
    <span className={formStyles.label}>
      {field.label}
      {field.unit ? ` (${field.unit})` : ""}
      {field.required ? " *" : ""}
    </span>
  );
}

function RegisterListInput({
  field,
  value,
  onChange,
  disabled,
}: {
  field: CommandParamField;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className={s.field}>
      <FieldLabel field={field} />
      {value.map((register, i) => (
        <div key={i} className={s.inlineRow}>
          <TextInput
            value={register}
            placeholder={t("pro.commands.params.registerPlaceholder")}
            disabled={disabled}
            onChange={(e) => onChange(value.map((r, j) => (j === i ? e.target.value : r)))}
          />
          {value.length > 1 && (
            <button
              type="button"
              className={s.iconButton}
              aria-label={t("pro.commands.params.removeRow")}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              disabled={disabled}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
      <div>
        <button type="button" className={s.linkButton} onClick={() => onChange([...value, ""])} disabled={disabled}>
          <Plus size={12} /> {t("pro.commands.params.addRegister")}
        </button>
      </div>
      {field.help && <span className={s.help}>{field.help}</span>}
    </div>
  );
}

function RegisterValuesInput({
  field,
  value,
  onChange,
  disabled,
}: {
  field: CommandParamField;
  value: RegisterValue[];
  onChange: (next: RegisterValue[]) => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const update = (i: number, patch: Partial<RegisterValue>) =>
    onChange(value.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  return (
    <div className={s.field}>
      <FieldLabel field={field} />
      {value.map((row, i) => (
        <div key={i} className={s.registerRow}>
          <TextInput
            label={i === 0 ? t("pro.commands.params.register") : undefined}
            value={row.register}
            placeholder={t("pro.commands.params.registerPlaceholder")}
            disabled={disabled}
            onChange={(e) => update(i, { register: e.target.value })}
          />
          <TextInput
            label={i === 0 ? t("pro.commands.params.value") : undefined}
            value={row.value}
            inputMode="numeric"
            disabled={disabled}
            onChange={(e) => update(i, { value: e.target.value })}
          />
          <button
            type="button"
            className={s.iconButton}
            aria-label={t("pro.commands.params.removeRow")}
            onClick={() => onChange(value.length > 1 ? value.filter((_, j) => j !== i) : [{ register: "", value: "" }])}
            disabled={disabled}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <div>
        <button
          type="button"
          className={s.linkButton}
          onClick={() => onChange([...value, { register: "", value: "" }])}
          disabled={disabled}
        >
          <Plus size={12} /> {t("pro.commands.params.addRegister")}
        </button>
      </div>
      {field.help && <span className={s.help}>{field.help}</span>}
    </div>
  );
}

export function CommandParamForm({
  fields,
  values,
  onChange,
  disabled,
  errorField,
}: {
  fields: CommandParamField[];
  values: CommandParamValues;
  onChange: (next: CommandParamValues) => void;
  disabled?: boolean;
  errorField?: string | null;
}) {
  const { t } = useI18n();
  if (fields.length === 0) return null;
  const set = (key: string, value: unknown) => onChange({ ...values, [key]: value });
  const listFields = fields.filter((f) => f.type === "register_list" || f.type === "register_values");
  const simpleFields = fields.filter((f) => !listFields.includes(f));

  return (
    <div className={s.field} style={{ gap: 14 }}>
      {simpleFields.length > 0 && (
        <div className={s.fieldGrid}>
          {simpleFields.map((field) => {
            const value = values[field.key];
            if (field.type === "boolean") {
              return (
                <label key={field.key} className={s.field}>
                  <FieldLabel field={field} />
                  <Switch
                    checked={Boolean(value)}
                    onCheckedChange={(checked: boolean) => set(field.key, checked)}
                    disabled={disabled}
                  />
                  {field.help && <span className={s.help}>{field.help}</span>}
                </label>
              );
            }
            if (field.type === "enum") {
              return (
                <label key={field.key} className={s.field}>
                  <FieldLabel field={field} />
                  <select
                    className={formStyles.input}
                    value={String(value ?? "")}
                    onChange={(e) => set(field.key, e.target.value)}
                    disabled={disabled}
                  >
                    <option value="">{t("pro.commands.params.choose")}</option>
                    {field.options?.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {field.help && <span className={s.help}>{field.help}</span>}
                </label>
              );
            }
            const numeric = field.type === "integer" || field.type === "number";
            const range =
              numeric && (field.min != null || field.max != null)
                ? t("pro.commands.params.range", { min: field.min ?? "−∞", max: field.max ?? "∞" })
                : null;
            return (
              <div key={field.key} className={s.field}>
                <FieldLabel field={field} />
                <TextInput
                  value={String(value ?? "")}
                  inputMode={numeric ? "decimal" : undefined}
                  placeholder={field.type === "register" ? t("pro.commands.params.registerPlaceholder") : undefined}
                  onChange={(e) => set(field.key, e.target.value)}
                  disabled={disabled}
                  error={errorField === field.key ? t("pro.commands.params.invalid") : undefined}
                />
                {(field.help || range) && (
                  <span className={s.help}>{[field.help, range].filter(Boolean).join(" · ")}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {listFields.map((field) =>
        field.type === "register_list" ? (
          <RegisterListInput
            key={field.key}
            field={field}
            value={values[field.key] as string[]}
            onChange={(next) => set(field.key, next)}
            disabled={disabled}
          />
        ) : (
          <RegisterValuesInput
            key={field.key}
            field={field}
            value={values[field.key] as RegisterValue[]}
            onChange={(next) => set(field.key, next)}
            disabled={disabled}
          />
        ),
      )}
    </div>
  );
}
